import type { AudioAsset, PhotoAsset, Species, SoundType } from '../../types';
import { confusablesFor } from '../../types';
import { getSpecies, packPool } from '../region';
import { studyList } from '../ignored';
import { studyRemainder } from '../fsrs';
import { narrow } from './focus';
import { pickDistractors } from './distractors';
import { DEFAULT_ROUND_LEN, newBirdShare } from './settings';
import { drawMirror } from '../media/mirror';

// Sound Match: Sound ID run backwards. The bird is named and pictured, and the
// question is which of two recordings is its voice.
//
// Sound ID asks a learner to pull one name out of four while a clip plays,
// which is the field problem — but it is also four things at once, and a bird
// you half-know is answered by eliminating the three you know better. Holding
// ONE recording against ONE other makes the comparison the whole question: this
// trill or that trill, and no way to get there except by knowing the bird.
//
// So the wrong clip is never a random bird. It is the closest confounder the
// region has — the head of this species' own acoustic cluster — recorded in the
// SAME voice as the right answer, because a song against a call compares
// nothing and hands over the answer on length alone.
//
// No fourth card: a question grades the ordinary `code:sound` card, exactly as
// the Field Test's sound questions do. It is the same knowledge, asked from the
// other side.

export interface SoundMatchQuestion {
  /** The species being tested — the bird pictured. */
  target: Species;
  /** The picture that is the prompt. */
  photo: PhotoAsset;
  /** Whether it goes up mirrored — see core/media/mirror.ts. */
  mirrored: boolean;
  /** The bird the wrong clip belongs to: the target's closest confounder. */
  decoy: Species;
  /** Both clips in display order — A then B. */
  clips: AudioAsset[];
  /** The species each clip belongs to, parallel to `clips`. */
  codes: string[];
  /** Index into `clips` of the target's own recording. */
  answer: number;
  /** The voice both clips are in. Revealed with the answer, never before. */
  type: SoundType;
}

/** Whether a recording is one of the voices a round asked for. */
function allowed(a: AudioAsset, voices?: readonly SoundType[]): boolean {
  return !voices || voices.includes(a.type);
}

/**
 * Species this mode can ask about: a picture to show, and a recording in one of
 * the voices the round allows.
 *
 * This, not the full region, is what the study queue draws from — the same
 * reason `soundPool()` and `photoPool()` narrow. It is deliberately a superset
 * of what can actually be built: whether a species has a confounder able to
 * answer in the same voice depends on the pack, and a question that can't be
 * built is dropped when the session is assembled.
 */
export function soundMatchPool(voices?: readonly SoundType[]): string[] {
  return studyList()
    .filter((s) => s.photos.length > 0 && s.audio.some((a) => allowed(a, voices)))
    .map((s) => s.code);
}

function pickPhoto(sp: Species, rnd: () => number): PhotoAsset | undefined {
  if (!sp.photos.length) return undefined;
  return sp.photos[Math.floor(rnd() * sp.photos.length)];
}

/** Every recording a species has in one particular voice. */
function inVoice(code: string, type: SoundType): AudioAsset[] {
  return getSpecies(code)?.audio.filter((a) => a.type === type) ?? [];
}

/**
 * The bird whose voice this one is most likely to be taken for, out of those
 * that can answer in `type`.
 *
 * The acoustic cluster is written best-first — the pipeline scores it and sorts
 * descending (see tools/pipeline/lib/confusables.ts) — so the closest
 * confounder is simply the first entry that is available, and it is the same
 * bird every time this species comes up in this voice. That repetition is the
 * point: two options is a coin flip until the learner can actually separate the
 * pair, and the pair is worth separating.
 *
 * Where the cluster has nothing to offer — a bird with no close relatives in
 * the region, or one whose relatives have no recording of this voice — the
 * graded cascade takes over: family, then taxonomic neighbourhood, then the
 * nearest thing there is. A distant confounder is a weaker question, not a
 * broken one; a random one would be a free mark, which is exactly what
 * `pickDistractors` exists to avoid.
 */
function pickDecoy(
  code: string,
  type: SoundType,
  pool: string[],
  rnd: () => number,
): string | undefined {
  const able = pool.filter((c) => c !== code && inVoice(c, type).length > 0);
  if (!able.length) return undefined;
  const offer = new Set(able);
  const nearest = confusablesFor(getSpecies(code), 'sound').find((c) => offer.has(c));
  if (nearest) return nearest;
  return pickDistractors(code, able, 1, 'sound', rnd)[0];
}

/**
 * Build a single Sound Match question for a known species code.
 *
 * The clip is drawn uniformly over the target's own recordings — the ones in an
 * allowed voice that some other bird can also answer in — so a bird with six
 * calls and one song is mostly heard calling, the same weighting `pickAudio()`
 * gives Sound ID. Returns null when the pack can't produce the pair: no photo,
 * no allowed recording, or nothing to hold it against.
 */
export function buildSoundMatchQuestion(
  code: string,
  rnd: () => number = Math.random,
  voices?: readonly SoundType[],
): SoundMatchQuestion | null {
  const target = getSpecies(code);
  if (!target) return null;
  const photo = pickPhoto(target, rnd);
  if (!photo) return null;

  const mine = target.audio.filter((a) => allowed(a, voices));
  if (!mine.length) return null;
  // Decoys need a recording, never a picture — only their sound is shown — so
  // the pool here is the whole pack rather than `soundMatchPool()`.
  const pool = packPool(
    code,
    studyList()
      .filter((s) => s.audio.length > 0)
      .map((s) => s.code),
  );

  // One decoy per voice the target has, so the clip can then be chosen over the
  // recordings rather than over the voices.
  const answering = new Map<SoundType, string>();
  for (const type of new Set(mine.map((a) => a.type))) {
    const decoy = pickDecoy(code, type, pool, rnd);
    if (decoy) answering.set(type, decoy);
  }
  const usable = mine.filter((a) => answering.has(a.type));
  if (!usable.length) return null;

  const clip = usable[Math.floor(rnd() * usable.length)];
  const decoyCode = answering.get(clip.type)!;
  const decoy = getSpecies(decoyCode);
  if (!decoy) return null;
  const theirs = inVoice(decoyCode, clip.type);
  const other = theirs[Math.floor(rnd() * theirs.length)];

  // A or B, evenly. The one thing this mode must never develop is a side.
  const answer = rnd() < 0.5 ? 0 : 1;
  return {
    target,
    photo,
    // Drawn last, so every draw above lands where it did before mirroring existed.
    mirrored: drawMirror(rnd),
    decoy,
    clips: answer === 0 ? [clip, other] : [other, clip],
    codes: answer === 0 ? [code, decoyCode] : [decoyCode, code],
    answer,
    type: clip.type,
  };
}

/**
 * Build a Sound Match session from the FSRS study queue, exactly as the other
 * modes do — species this one can't ask about are never queued, and the ones it
 * turns out it can't build a pair for are dropped.
 *
 * `voices` is what the round may play; `WITHOUT_CALLS` from quiz/sound.ts is
 * the easy mode, and leaving it undefined puts calls in the mix. `done` is a
 * species already asked outside the session, and `len` the round length the
 * setup screen chose.
 */
export async function buildSoundMatchSession(
  now: Date = new Date(),
  rnd: () => number = Math.random,
  voices?: readonly SoundType[],
  done?: string,
  len: number = DEFAULT_ROUND_LEN,
  /**
   * The birds this round is confined to, when it was started from a filtered
   * list rather than from the study grid — see core/quiz/focus.ts. Undefined is
   * an ordinary round over everything this mode can ask about.
   */
  focus?: readonly string[],
): Promise<SoundMatchQuestion[]> {
  const queue = await studyRemainder(
    'sound',
    narrow(soundMatchPool(voices), focus),
    now,
    len,
    done,
    // Keyed to this mode rather than to the sound cards it grades: the setup
    // screen the learner just chose on was Sound Match's own.
    await newBirdShare('match'),
  );
  const questions: SoundMatchQuestion[] = [];
  for (const code of queue) {
    const q = buildSoundMatchQuestion(code, rnd, voices);
    if (q) questions.push(q);
  }
  return questions;
}
